/**
 * Uzávěrky / Směny — the shift close-out screen.
 *
 * This is the screen a manager opens to check the money, so it is built
 * around one question: for a given shift, how much came in, in what form, and
 * who was on. Everything else is secondary and is placed where it does not
 * compete with the figures.
 *
 * The layout borrows deliberately from two screens the staff already know.
 * The summary card at the top is the dashboard's Pokladna card, class for
 * class — same labels, same order, same total rule underneath — because that
 * card is where people already read money in this application, and giving the
 * same numbers a second visual language would be a small tax paid on every
 * single reading. The table below is the Rezervace grid: same toolbar, same
 * pager, same click-a-row-to-open-it habit.
 *
 * The row expands rather than navigating. A shift is not a record with a page
 * of its own — it is eleven numbers — and eleven numbers do not justify losing
 * the list you were comparing against. Opening one closes the previous one, so
 * the screen never grows a second scrollbar's worth of detail.
 *
 * A note on the times shown. `closed` is stored in UTC by the legacy
 * application while `started` is stored in Prague local time; the API corrects
 * the end of every window before computing anything, and the expanded row
 * shows both values side by side. That is not a debugging leftover — it is
 * the answer to the first question a manager will ask, which is why this
 * screen's numbers do not match the old one's.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MAX_PAGE, type BalancePage, type BalanceRow } from '@smile/shared';
import { api } from '../api/client';
import { CZK, ROLE_LABEL } from '../lib/format';
import {
  dateParam,
  pageList,
  pageParam,
  PAST_QUICK_RANGES,
  quickRangeKey,
  shiftDuration,
  splitDt,
  todayISO,
} from '../lib/filters';
import { Footer } from '../components/Footer';

const PER_PAGE = 20;

/**
 * Two weeks, matching the Rezervace grid's default window. A manager landing
 * here wants the last few shifts, and "the same fortnight as everywhere else"
 * is one less thing to hold in your head when comparing two screens.
 */
const DEFAULT_DAYS = 14;

const money = (n: number) => `${CZK.format(Math.round(n))} Kč`;

const KIND_LABEL: Record<BalanceRow['kind'], string> = {
  day: 'Denní',
  night: 'Noční',
  other: 'Mimo rozpis',
};

/* Trvání, and the twelve-hour rule that decides whether it is flagged, live
   in lib/filters.ts — see shiftDuration there. Thresholds that decide what a
   manager is warned about belong somewhere a harness can execute them. */

/** GoPay + BitPay + faktura: money that was already in before the barrier. */
const prepaid = (r: BalanceRow) => r.till.gopay + r.till.bitpay + r.till.invoice;

/* ------------------------------------------------------------------ *
 * Cells
 * ------------------------------------------------------------------ */

function Smena({ r }: { r: BalanceRow }) {
  const from = splitDt(r.started);
  const to = splitDt(r.closed);
  // The end date is printed only when it differs from the start date, which
  // for a night shift it always does. Repeating it on a day shift is noise.
  const crossed = from.d !== to.d;
  return (
    <td>
      <span className={r.kind === 'night' ? 'smena noc' : r.kind === 'day' ? 'smena den' : 'smena jina'}>
        {KIND_LABEL[r.kind]}
      </span>
      <div className="sub">
        <span className="time">{from.d}</span> {from.t} → {crossed ? `${to.d} ` : ''}
        {to.t}
      </div>
    </td>
  );
}

function Obsazeni({ r }: { r: BalanceRow }) {
  // `closedBy` is who pressed the button; `staff` is who worked. They are
  // usually the same one person, and when they are not, the difference is the
  // point of the column — so the closer is always shown first and marked.
  const others = r.staff.filter((s) => s.id !== r.closedById);
  return (
    <td className="obsazeni">
      <b className="name">{r.closedByName || '—'}</b>
      {others.map((s) => (
        <span key={s.id} className="crew" title={ROLE_LABEL[s.role] ?? s.role}>
          {s.name}
        </span>
      ))}
    </td>
  );
}

/**
 * The expanded row. Same labels and same order as the dashboard's Pokladna
 * card, because it is the same money split — a manager who has learned to
 * read one has learned to read the other.
 */
function Detail({ r }: { r: BalanceRow }) {
  // These labels are copied from the dashboard's Pokladna card word for word,
  // including "Fakturace CK".
  // They are not better labels than the ones I would have written; they are
  // the labels the staff already read every shift, and a second wording for
  // the same six numbers is a tax paid on every single reading.
  const rows: [string, number, string?][] = [
    ['Hotovost', r.till.cash],
    ['Kartou při odjezdu', r.till.card],
    ['Automat', r.till.onsiteOther, 'Platby přes platební automat (auto_cash, auto_card), jak je počítá stará uzávěrka.'],
    ['GoPay předplaceno', r.till.gopay],
    ['Fakturace CK', r.till.invoice],
  ];
  return (
    <tr className="detailrow">
      <td colSpan={8}>
        <div className="smenadetail">
          <div className="pokladna card">
            <h3>
              Pokladna směny{' '}
              {/* The legacy printed this sheet after every close; here it is
                  a click on the open row, in a tab that prints itself. */}
              <a className="btn sm" href={`/uzaverka/${r.id}`} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
                Tisk uzávěrky
              </a>
            </h3>
            {rows.map(([label, value, title]) =>
              // The automat row is hidden when it is zero, exactly
              // as on the dashboard: an always-visible 0 Kč next to a label
              // nobody recognises reads as a fault in the screen.
              value === 0 && label === 'Automat' ? null : (
                <div className="row" key={label}>
                  <span className="lbl" title={title}>
                    {label}
                  </span>
                  <span className="val">{money(value)}</span>
                </div>
              ),
            )}
            <div className="row total">
              <span className="lbl">Celkem</span>
              <span className="val">{money(r.till.total)}</span>
            </div>
            <div className="basis">
              Základ: {CZK.format(r.till.cars)} vozů, které odjely během této
              směny. Platba je přiřazena ke směně podle skutečného odjezdu vozu
              — čas samotné platby databáze neuchovává.
            </div>
          </div>

          <div className="smenameta">
            {r.note && (
              <div className="field">
                <dt>Poznámka k uzávěrce</dt>
                <dd className="note">{r.note}</dd>
              </div>
            )}
            <div className="field">
              <dt>Okno směny</dt>
              <dd>
                {r.started} → {r.closed}
              </dd>
            </div>
            <div className="field">
              <dt>Uloženo v databázi</dt>
              <dd>
                <span className="was">{r.closedStored}</span>
              </dd>
            </div>
            <div className="field">
              <dt>Rezervace uzamčeny</dt>
              <dd>{r.locked ? 'Ano' : 'Ne'}</dd>
            </div>
            <p className="tzwarn">
              Starý systém ukládá konec směny v čase UTC, začátek v pražském
              čase. Konec je proto přepočítán (+1 h v zimě, +2 h v létě).
              Bez přepočtu by tržba za poslední hodiny směny spadla do směny
              následující.
            </p>
          </div>
        </div>
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------ *
 * Screen
 * ------------------------------------------------------------------ */

export function Smeny() {
  const [sp, setSp] = useSearchParams();

  /* All three come out of the address bar, so none of them is trusted. The
     API's schema tests `od` and `do` against a 'YYYY-MM-DD' regex and caps
     `strana` at MAX_PAGE; anything this screen forwards that fails either one
     comes back 400 and is reported here as "Uzávěrky se nepodařilo načíst",
     which is a sentence about a server being down. See pageParam. */
  const from = dateParam(sp.get('od'), todayISO(-DEFAULT_DAYS));
  const to = dateParam(sp.get('do'), todayISO());
  const page = pageParam(sp.get('strana'), MAX_PAGE);

  /* `od` after `do` is reachable from the address bar and, in some browsers,
     by typing into the date field, whose `max`/`min` only stop the picker.
     The API answers it with an empty page, and the table would then say no
     shift was closed in this period — true, and the wrong thing to tell
     somebody who has just been handed a backwards range by a bad paste. */
  const backwards = from > to;

  /* Read the clock once per render rather than once per button: six buttons
     each calling new Date() could straddle midnight and light up two of
     themselves. This screen keeps loose `from`/`to` strings instead of a
     Filters object, so the shape is built here rather than passed through. */
  const activeQuick = quickRangeKey({ from, to }, new Date(), PAST_QUICK_RANGES);

  const [open, setOpen] = useState<number | null>(null);

  const apply = (patch: Record<string, string | number | null>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === '') next.delete(k);
      else next.set(k, String(v));
    }
    // Any change to the window invalidates the page number: staying on page 7
    // of a filter that now has two pages shows an empty table and reads as a
    // broken screen rather than as a stale page number.
    if (!('strana' in patch)) next.delete('strana');
    setSp(next, { replace: true });
  };

  const { data, isPending, isFetching, error, refetch } = useQuery<BalancePage>({
    queryKey: ['balances', from, to, page],
    queryFn: () => api.balances({ from, to, page, perPage: PER_PAGE }),
  });

  // An expanded row belongs to the page it was opened on. Leaving the id set
  // across a page change would either expand nothing or, worse, expand a
  // different shift that happens to sit in the same position.
  useEffect(() => {
    setOpen(null);
  }, [from, to, page]);

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1),
    [data],
  );

  const totals = data?.totals;

  return (
    <div className="container">
      <div className="pagehead">
        <h1>Uzávěrky</h1>
        <span className="sub">
          {data ? `${CZK.format(data.total)} směn ve vybraném období` : '…'}
          {isFetching && !isPending ? ' · aktualizuji' : ''}
        </span>
      </div>

      {/* The summary card is the dashboard's Pokladna, and it is labelled with
          exactly what it sums: the shifts on screen. Summing the whole
          filtered range would mean assuming the shifts in it are contiguous
          and non-overlapping, which the thirteen-year history does not
          promise — and a total that is quietly wrong is worse than one that
          is narrow and says so. */}
      <div className="toprow">
        <div className="card pokladna smenatotal">
          <h3>Součet zobrazených směn</h3>
          <div className="row">
            <span className="lbl">Hotovost</span>
            <span className="val">{totals ? money(totals.cash) : '…'}</span>
          </div>
          <div className="row">
            <span className="lbl">Kartou při odjezdu</span>
            <span className="val">{totals ? money(totals.card) : '…'}</span>
          </div>
          {!!totals?.onsiteOther && (
            <div className="row">
              <span className="lbl" title="Platba na místě bez zaznamenaného způsobu.">
                Automat
              </span>
              <span className="val">{money(totals.onsiteOther)}</span>
            </div>
          )}
          <div className="row">
            <span className="lbl">Předplaceno</span>
            <span className="val">
              {totals ? money(totals.gopay + totals.bitpay + totals.invoice) : '…'}
            </span>
          </div>
          <div className="row total">
            <span className="lbl">Celkem</span>
            <span className="val">{totals ? money(totals.total) : '…'}</span>
          </div>
          <div className="basis">
            Základ: {totals ? CZK.format(totals.cars) : '…'} vozů ve směnách na
            této straně (strana {page} z {CZK.format(totalPages)}).
          </div>
        </div>

        <div className="card zpravy">
          <div className="h">
            <h2>Uzávěrka směny</h2>
          </div>
          <div className="empty">
            Směna se uzavírá tlačítkem <b>Uzavřít směnu</b> pod pokladnou na Dashboardu — tam, kde
            bylo i ve starém systému. Zde je přehled: čísla se počítají z reálných odjezdů
            v okně směny, ne z ručně zapsané částky.
          </div>
        </div>
      </div>

      <div className="card tablecard">
        <div className="toolbar">
          <label className="chk" htmlFor="sod">
            Od
          </label>
          <input
            id="sod"
            type="date"
            value={from}
            max={to}
            onChange={(e) => apply({ od: e.target.value })}
          />
          <label className="chk" htmlFor="sdo">
            do
          </label>
          <input
            id="sdo"
            type="date"
            value={to}
            min={from}
            onChange={(e) => apply({ do: e.target.value })}
          />
          <span className="sep" />
          {/* These were two plain buttons reading "Posledních 14 dní" and
              "30 dní" — the same idea, in a different shape from the strip on
              Rezervace and Statistiky, and with no way to tell which window
              you were on. Same control, same place, same look now, and the
              active one is lit. The 14-day entry is still first because it is
              this screen's default and a manager opening it should see the
              button that describes what is on screen already pressed. */}
          <div className="quickrange" role="group" aria-label="Rychlý výběr období">
            {PAST_QUICK_RANGES.map((qr) => (
              <button
                key={qr.key}
                type="button"
                className={activeQuick === qr.key ? 'qbtn on' : 'qbtn'}
                title={qr.title}
                aria-pressed={activeQuick === qr.key}
                onClick={() => {
                  const r = qr.range(new Date());
                  apply({ od: r.from, do: r.to });
                }}
              >
                {qr.label}
              </button>
            ))}
          </div>
        </div>

        <div className="tablewrap">
          <table className="smenytbl">
            <thead>
              <tr>
                <th>Směna</th>
                <th>Trvání</th>
                <th>Obsazení</th>
                <th className="num">Aut</th>
                <th className="num">Hotovost</th>
                <th className="num">Karta</th>
                <th className="num">Předplaceno</th>
                <th className="num">Celkem</th>
              </tr>
            </thead>
            <tbody>
              {/* Announced, quotable, recoverable — the same three properties
                  the Statistiky card has. Reloading the page instead would
                  throw away the date window, which on this screen is the
                  whole of what the user set. */}
              {error && (
                <tr>
                  <td colSpan={8} className="state" role="alert">
                    Uzávěrky se nepodařilo načíst.
                    {error instanceof Error && error.message ? ` (${error.message})` : ''}{' '}
                    <button className="btn sm" type="button" onClick={() => refetch()}>
                      Zkusit znovu
                    </button>
                  </td>
                </tr>
              )}
              {isPending &&
                !error &&
                Array.from({ length: 8 }, (_, i) => (
                  <tr key={`s${i}`}>
                    <td colSpan={8}>
                      <div className="skeleton" />
                    </td>
                  </tr>
                ))}
              {data?.rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="state">
                    {backwards
                      ? 'Období je zadané obráceně — „od“ je později než „do“.'
                      : 'V tomto období nebyla uzavřena žádná směna.'}
                  </td>
                </tr>
              )}
              {data?.rows.map((r) => {
                const d = shiftDuration(r.minutes);
                const isOpen = open === r.id;
                return [
                  <tr
                    key={r.id}
                    className={isOpen ? 'click on' : 'click'}
                    /* Same as the Klienti rows, and for the same reason: a row
                       that says aria-expanded and cannot be reached by Tab
                       announces a control that is not there. Space as well as
                       Enter, because this row opens in place rather than
                       navigating; both prevented, or Space scrolls the page
                       out from under the row it has just opened. */
                    tabIndex={0}
                    role="button"
                    aria-expanded={isOpen}
                    aria-label={`${KIND_LABEL[r.kind]} ${splitDt(r.started).d} — ${
                      isOpen ? 'skrýt' : 'zobrazit'
                    } detail`}
                    onClick={() => setOpen(isOpen ? null : r.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpen(isOpen ? null : r.id);
                      }
                    }}
                  >
                    <Smena r={r} />
                    <td>
                      <span className={d.odd ? 'trvani odd' : 'trvani'} title={d.odd ? 'Směna mimo obvyklých 12 hodin.' : undefined}>
                        {d.text}
                      </span>
                    </td>
                    <Obsazeni r={r} />
                    <td className="num">{CZK.format(r.till.cars)}</td>
                    <td className="num">{money(r.till.cash)}</td>
                    <td className="num">{money(r.till.card)}</td>
                    <td className="num">{money(prepaid(r))}</td>
                    <td className="num strong">
                      {money(r.till.total)}
                      {/* The four money columns are Hotovost, Karta,
                          Předplaceno and this one. `onsiteOther` is the
                          paystation's take (auto_cash / auto_card), a bucket
                          of the legacy till (Balance::getBalanceData, read
                          18 October 2026); zero on most rows, so a sub-line
                          rather than a ninth column. The summary card above
                          and the expanded row below show it too. */}
                      {r.till.onsiteOther !== 0 && (
                        <span
                          className="sub"
                          title="Platba na místě bez zaznamenaného způsobu — není v žádném ze sloupců vlevo."
                        >
                          z toho {money(r.till.onsiteOther)} automat
                        </span>
                      )}
                    </td>
                  </tr>,
                  isOpen ? <Detail key={`d${r.id}`} r={r} /> : null,
                ];
              })}
            </tbody>
          </table>
        </div>

        <div className="pager">
          <span>
            {data
              ? `Strana ${data.page} z ${CZK.format(totalPages)} · ${CZK.format(data.total)} směn`
              : '…'}
          </span>
          <div className="pages">
            <button
              onClick={() => apply({ strana: Math.max(1, page - 1) })}
              disabled={page <= 1}
              aria-label="Předchozí strana"
            >
              ‹
            </button>
            {pageList(page, totalPages).map((p, i) =>
              p === '…' ? (
                <span key={`gap${i}`} className="gap">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  className={p === page ? 'on' : undefined}
                  aria-current={p === page ? 'page' : undefined}
                  onClick={() => apply({ strana: p })}
                >
                  {p}
                </button>
              ),
            )}
            <button
              onClick={() => apply({ strana: Math.min(totalPages, page + 1) })}
              disabled={page >= totalPages}
              aria-label="Další strana"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
